import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import CampaignsPage from './CampaignsPage';
import SegmentsPage from './SegmentsPage';
import StatisticsPage from './StatisticsPage';
import UsersPage from './UsersPage';
import CampaignFieldDistribution from '../components/campaigns/CampaignFieldDistribution';

const ANALYTICS_IFRAME_URL =
  'https://oyster-huge-cockatoo.ngrok-free.app/public/dashboard/82148e72-ad28-4043-b664-b2e9aee2bd7c?%25D0%25B4%25D0%25B6%25D0%25B5%25D1%2580%25D0%25B5%25D0%25BB%25D0%25BE=&%25D0%25BF%25D0%25B5%25D1%2580%25D1%2596%25D0%25BE%25D0%25B4=2025-11-10~2025-12-14&%25D1%2580%25D0%25B5%25D0%25BA%25D0%25BB%25D0%25B0%25D0%25BC%25D0%25BE%25D0%25B4%25D0%25B0%25D0%25B2%25D0%25B5%25D1%2586%25D1%258C=KONTRAKT_18-24&%25D1%2582%25D0%25BC%252F%25D0%25BF%25D1%2580%25D0%25BE%25D0%25B4%25D1%2583%25D0%25BA%25D1%2582=DRONES_LUT&%25D1%2582%25D0%25BC%252F%25D0%25BF%25D1%2580%25D0%25BE%25D0%25B4%25D1%2583%25D0%25BA%25D1%2582=DRONES_MAVIC&%25D1%2582%25D0%25BC%252F%25D0%25BF%25D1%2580%25D0%25BE%25D0%25B4%25D1%2583%25D0%25BA%25D1%2582=DRONES_SHARK&%25D1%2582%25D0%25BC%252F%25D0%25BF%25D1%2580%25D0%25BE%25D0%25B4%25D1%2583%25D0%25BA%25D1%2582=DRONES_VAMPIR';


const CampaignAnalytics = ({ analytics, segments, days }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const getSegmentName = (segmentId) => {
    const segment = segments.find(s => s.uuid === segmentId);
    return segment?.name || segmentId?.slice(0, 8) || 'Unknown';
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [analytics?.dailyBreakdown?.length]);

  const totalDays = analytics.dailyBreakdown?.length || 0;
  const totalPages = Math.ceil(totalDays / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDays = analytics.dailyBreakdown?.slice(startIndex, endIndex) || [];

  return (
    <div style={{ marginTop: '2rem' }}>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Ads Served</div>
          <div className="stat-value">{analytics.totalServed.toLocaleString()}</div>
          <div className="stat-change neutral">{days > 0 ? `Last ${days} days` : 'All time'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Unique Devices</div>
          <div className="stat-value">{analytics.uniqueDevices.toLocaleString()}</div>
          <div className="stat-change positive">
            {analytics.totalServed > 0
              ? `${(analytics.totalServed / analytics.uniqueDevices).toFixed(1)} avg per device`
              : 'No data'}
          </div>
        </div>
      </div>

      {analytics.dailyBreakdown?.length > 0 && (
        <div style={{ marginTop: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>Daily Breakdown</h3>
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                <span>Page {currentPage} of {totalPages}</span>
              </div>
            )}
          </div>
          <div className="campaigns-grid">
            <table className="campaigns-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Served</th>
                  <th>Unique Devices</th>
                  <th>Avg per Device</th>
                </tr>
              </thead>
              <tbody>
                {paginatedDays.map((day) => (
                  <tr key={day.date}>
                    <td>{new Date(day.date).toLocaleDateString()}</td>
                    <td>{day.served.toLocaleString()}</td>
                    <td>{day.uniqueDevices.toLocaleString()}</td>
                    <td>{day.uniqueDevices > 0 ? (day.served / day.uniqueDevices).toFixed(2) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                className="btn"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                style={{ minWidth: '80px' }}
              >
                Previous
              </button>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        className={`btn ${currentPage === page ? 'btn-primary' : ''}`}
                        onClick={() => setCurrentPage(page)}
                        style={{
                          minWidth: '36px',
                          padding: '0.5rem',
                          fontSize: '0.875rem',
                          backgroundColor: currentPage === page ? 'var(--primary)' : 'var(--bg-secondary)',
                          color: currentPage === page ? 'white' : 'var(--text-primary)',
                          border: '1px solid var(--border)',
                        }}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 2 ||
                    page === currentPage + 2
                  ) {
                    return <span key={page} style={{ padding: '0.5rem' }}>...</span>;
                  }
                  return null;
                })}
              </div>
              <button
                className="btn"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                style={{ minWidth: '80px' }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {analytics.segmentBreakdown?.length > 0 && (
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>By Segment</h3>
          <div className="campaigns-grid">
            <table className="campaigns-table">
              <thead>
                <tr>
                  <th>Segment</th>
                  <th>Served</th>
                  <th>Unique Devices</th>
                </tr>
              </thead>
              <tbody>
                {analytics.segmentBreakdown.map((seg) => (
                  <tr key={seg.segmentId}>
                    <td>{getSegmentName(seg.segmentId)}</td>
                    <td>{seg.served.toLocaleString()}</td>
                    <td>{seg.uniqueDevices.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {analytics.totalServed === 0 && (
        <div className="empty-state" style={{ marginTop: '2rem' }}>
          <h3>No data yet</h3>
          <p>This campaign hasn't served any ads in the selected period.</p>
        </div>
      )}
    </div>
  );
};

const OverviewPage = () => {
  const { campaigns, segments, performRequest } = useApp();
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [days, setDays] = useState(0); // 0 means all time
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalAdsServed, setTotalAdsServed] = useState(0);
  const [totalAdsServedLoading, setTotalAdsServedLoading] = useState(true);

  const activeCampaigns = campaigns.filter(c => c.status === 'active').length;
  const totalSpend = campaigns.reduce((sum, c) => sum + c.spent, 0) || 0;
  const totalClicks = campaigns.reduce((sum, c) => sum + c.clicks, 0) || 0;
  // const ctr = totalAdsServed > 0 ? ((totalClicks / totalAdsServed) * 100).toFixed(2) : '0.00';

  const [iframeLoaded, setIframeLoaded] = useState(false);

  const fetchTotalAdsServed = useCallback(async () => {
    setTotalAdsServedLoading(true);
    try {
      const data = await performRequest('/statistics/total-ads-served');
      setTotalAdsServed(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch total ads served', error);
      setTotalAdsServed(0);
    } finally {
      setTotalAdsServedLoading(false);
    }
  }, [performRequest]);

  useEffect(() => {
    if (!selectedCampaign) {
      fetchTotalAdsServed();
    }
  }, [selectedCampaign, fetchTotalAdsServed]);

  const fetchAnalytics = useCallback(async () => {
    if (!selectedCampaign) {
      setAnalytics(null);
      return;
    }

    setLoading(true);
    try {
      const daysParam = days > 0 ? `days=${days}` : '';
      const data = await performRequest(`/campaigns/${selectedCampaign}/analytics${daysParam ? `?${daysParam}` : ''}`);
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to fetch campaign analytics', error);
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [selectedCampaign, days, performRequest]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return (
    <section className="content-section active">
      <div className="section-header">
        <div>
          <h2>Performance Overview</h2>
          <p>Quick metrics for your campaigns and audiences (Last 30 days)</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <select
            className="chart-filter-select"
            style={{ minWidth: '220px' }}
            value={selectedCampaign}
            onChange={(e) => setSelectedCampaign(e.target.value)}
          >
            <option value="">All Campaigns Overview</option>
            {campaigns.map((c) => (
              <option key={c.uuid} value={c.uuid}>
                {c.name}
              </option>
            ))}
          </select>
          {selectedCampaign && (
            <select
              className="chart-filter-select"
              style={{ minWidth: '120px' }}
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
            >
              <option value={0}>All time</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          )}
        </div>
      </div>

      {!selectedCampaign ? (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Campaigns</div>
            <div className="stat-value">{campaigns.length}</div>
            <div className="stat-change positive">{activeCampaigns} active</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Audience Segments</div>
            <div className="stat-value">{segments.length}</div>
            <div className="stat-change neutral">Ready to activate</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Impressions Served</div>
            <div className="stat-value">
              {totalAdsServedLoading ? '...' : totalAdsServed.toLocaleString()}
            </div>
            <div className="stat-change positive">{totalClicks.toLocaleString()} clicks</div>
          </div>
          {/* <div className="stat-card">
            <div className="stat-label">Spend To Date</div>
            <div className="stat-value">${Math.round(totalSpend).toLocaleString()}</div>
            <div className="stat-change neutral">{ctr}% CTR</div>
          </div> */}
        </div>
      ) : loading ? (
        <div className="chart-loading-state">
          <div className="spinner"></div>
          <p>Loading campaign analytics...</p>
        </div>
      ) : analytics ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <CampaignAnalytics analytics={analytics} segments={segments} days={days} />
          <CampaignFieldDistribution
            campaignId={selectedCampaign}
            campaignName={campaigns.find(c => c.uuid === selectedCampaign)?.name || 'Campaign'}
            days={days}
          />
        </div>
      ) : (
        <div className="empty-state">
          <h3>Unable to load analytics</h3>
          <p>Please try again later.</p>
        </div>
      )}

      <div style={{ marginTop: '2rem' }}>
        <div style={{ marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0 }}>External Analytics</h3>
          <p style={{ margin: '0.25rem 0 0', opacity: 0.8 }}>
            Dashboard embedded via iframe
          </p>
        </div>

        <div className="embedded-analytics-frame">
          {!iframeLoaded && (
            <div className="embedded-analytics-loading">
              <div className="spinner" />
              <p>Loading embedded dashboard...</p>
            </div>
          )}

          {!iframeLoaded && (
            <div className="embedded-analytics-loading">
              <p style={{ margin: 0, fontWeight: 600 }}>Dashboard can’t be embedded</p>
              <p style={{ margin: '0.5rem 0 0', opacity: 0.85 }}>
                Most likely blocked by X-Frame-Options / CSP. Open it in a new tab:
              </p>
              <a
                href={ANALYTICS_IFRAME_URL}
                target="_blank"
                rel="noreferrer"
                style={{ marginTop: '0.75rem', display: 'inline-block' }}
              >
                Open dashboard
              </a>
            </div>
          )}

          <iframe
            title="Analytics Dashboard"
            src={ANALYTICS_IFRAME_URL}
            loading="lazy"
            onLoad={() => setIframeLoaded(true)}
            style={{
              width: '100%',
              height: '100%',
              border: 0,
              opacity: iframeLoaded ? 1 : 0,
            }}
            allowFullScreen
          />
        </div>
      </div>
    </section>
  );
};

export default function Dashboard() {
  const { currentUser, currentUserType, logout } = useApp();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/dashboard', label: 'Overview', icon: '📊', end: true },
    { to: '/dashboard/campaigns', label: 'Campaigns', icon: '📈' },
    { to: '/dashboard/segments', label: 'Segments', icon: '👥' },
    { to: '/dashboard/statistics', label: 'Statistics', icon: '📊' },
    { to: '/dashboard/users', label: 'Users', icon: '👤', adminOnly: true }
  ];

  return (
    <div className="dashboard active">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <h2>Adlywise</h2>
          <p>CTV/OTT Advertising Network</p>
        </div>
        <ul className="sidebar-menu">
          {navItems
            .filter(item => !item.adminOnly || currentUserType === 'admin')
            .map(item => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => (isActive ? 'active' : undefined)}
                >
                  <span aria-hidden="true" className="sidebar-icon">{item.icon}</span>
                  {item.label}
                </NavLink>
              </li>
            ))}
        </ul>
        <div className="user-info">
          <p>Logged in as</p>
          <p><strong>{currentUser}</strong></p>
          <p style={{ textTransform: 'capitalize' }}><strong>{currentUserType}</strong></p>
          <button className="btn btn-logout" onClick={handleLogout}>Logout</button>
        </div>
      </aside>
      <main className="main-content">
        <header className="header">
          <div>
            <h1>Welcome back{currentUser ? `, ${currentUser}` : ''}</h1>
            <p>Monitor campaigns, audiences, and performance in real-time.</p>
          </div>
        </header>
        <Routes>
          <Route index element={<OverviewPage />} />
          <Route path="campaigns" element={<CampaignsPage />} />
          <Route path="segments" element={<SegmentsPage />} />
          <Route path="statistics" element={<StatisticsPage />} />
          {currentUserType === 'admin' && <Route path="users" element={<UsersPage />} />}
        </Routes>
      </main>
    </div>
  );
}
